/**
 * CommandConsoleLauncher.jsx · v2.7.63+E47
 * ─────────────────────────────────────────────────────────────────────
 * Four-window command console deployment button for dispatchers running
 * a multi-monitor rig (1080p to 4K). One click opens FOUR popup windows
 * — Live Map, Chat, Helpdesk, Emails — each sized to a quarter of the
 * available screen so a single 4K monitor can host all four consoles
 * without overlap. Two-monitor and single-monitor layouts fall back
 * gracefully.
 *
 * Auth flow: each popup inherits the current admin token via
 * localStorage (shared same-origin) — no re-login required, powered by
 * the cross-tab authSync we shipped in v2.7.63+E46.5. When any popup
 * loads, the OperatorBadge component reads /api/admin/me and renders
 * the operator's first-name / last-name / employee-id in the corner.
 *
 * URL flag: each popup is opened with `?console=1` in its URL so the
 * receiving page can:
 *   • Force a persistent OperatorBadge to render (see AdminLayoutShell)
 *   • Skip the sidebar entirely (space-saving in kiosk mode)
 *   • Emit a distinct data-console-window="{map|chat|helpdesk|email}"
 *     attribute on <body> for CSS overrides
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Monitor, Layout, Rocket, Info, Grid3x3, Sparkles } from "lucide-react";
import { toast } from "sonner";

const WINDOWS = [
  { id: "map",      label: "Live Map",  path: "/admin",           color: "text-amber-300" },
  { id: "chat",     label: "Chat",      path: "/admin/chat",      color: "text-emerald-300" },
  { id: "helpdesk", label: "Helpdesk",  path: "/admin/helpdesk",  color: "text-sky-300" },
  { id: "email",    label: "Emails",    path: "/admin/email",     color: "text-purple-300" },
];

/**
 * Compute the 4-quadrant coordinate for a given index (0..3) on the
 * primary screen. Returns {left, top, width, height} — pass into
 * `window.open` features string.
 *
 * Layout (quadrants):
 *   ┌─────────┬─────────┐
 *   │  0 MAP  │ 1 CHAT  │
 *   ├─────────┼─────────┤
 *   │ 2 HELP  │ 3 EMAIL │
 *   └─────────┴─────────┘
 */
function quadrantForIndex(idx) {
  // screen.availWidth / availHeight excludes OS chrome (taskbar, dock)
  // and is a good proxy for usable pixels on a single monitor.
  const sw = window.screen.availWidth  || window.screen.width  || 1920;
  const sh = window.screen.availHeight || window.screen.height || 1080;
  const w  = Math.floor(sw / 2);
  const h  = Math.floor(sh / 2);
  const col = idx % 2;
  const row = Math.floor(idx / 2);
  return {
    left:   col * w,
    top:    row * h,
    width:  w,
    height: h,
  };
}

/**
 * Alternative layout — one window per monitor when the user has 4
 * physical screens (or asks for that mode explicitly). Uses
 * window.screen.availLeft/availTop when available so each window jumps
 * to a specific monitor. Falls back to quadrant mode if unsupported.
 */
function acrossMonitorsForIndex(idx, monitorCount) {
  // Naive but effective: assume monitors are laid out left-to-right in
  // equal width. Screen 0 is the primary; screens 1..N-1 sit to its
  // right with the same width. This matches the most common 3–4-
  // monitor dispatcher rig ("all monitors landscape, side-by-side").
  const primaryW = window.screen.availWidth  || 1920;
  const primaryH = window.screen.availHeight || 1080;
  const monitor  = Math.min(idx, monitorCount - 1);
  return {
    left:   monitor * primaryW,
    top:    0,
    width:  primaryW,
    height: primaryH,
  };
}


export default function CommandConsoleLauncher() {
  const [busy, setBusy] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  const launch = (mode) => {
    setBusy(true);
    try {
      const base = window.location.origin;
      // v2.7.63+E48 · PWA-style single-window grid launch. Uses a
      // seamless standalone window (no toolbar/menubar) targeting the
      // dedicated ConsoleGridPage which hosts all four consoles inside
      // a single 2×2 iframe grid. Auth is inherited from localStorage
      // via the E46.5 cross-tab authSync; no re-login needed.
      if (mode === "grid") {
        const w = Math.min(1920, window.screen.availWidth  || 1920);
        const h = Math.min(1080, window.screen.availHeight || 1080);
        const features = [
          `width=${w}`, `height=${h}`,
          "menubar=no", "toolbar=no", "location=no", "status=no",
          "resizable=yes", "scrollbars=no",
        ].join(",");
        const popup = window.open(
          `${base}/admin/console?standalone=true`,
          "GridConsoleWindow",
          features,
        );
        if (!popup) {
          toast.error("Popup blocked. Allow popups for this site and retry.");
        } else {
          toast.success("Grid Console deployed · install as PWA via browser menu → 'Install app'");
          setTimeout(() => { try { popup.focus(); } catch (_e) { /* noop */ } }, 50);
        }
        return;
      }
      const totalScreens = mode === "monitors"
        ? Math.max(1, Math.min(4, Number(prompt("How many monitors? (2, 3, or 4)", "4")) || 4))
        : 1;
      const opened = [];
      const failed = [];
      WINDOWS.forEach((w, i) => {
        const q = mode === "monitors"
          ? acrossMonitorsForIndex(i, totalScreens)
          : quadrantForIndex(i);
        const features = [
          `left=${q.left}`,
          `top=${q.top}`,
          `width=${q.width}`,
          `height=${q.height}`,
          "toolbar=no",
          "menubar=no",
          "location=yes",
          "status=no",
          "resizable=yes",
          "scrollbars=yes",
        ].join(",");
        // ?console=1 tells the receiving page it's part of the command
        // console cluster so it can render the OperatorBadge overlay + skip
        // the sidebar for space efficiency.
        const url = `${base}${w.path}?console=${w.id}`;
        const popup = window.open(url, `rtg_console_${w.id}`, features);
        if (popup) {
          opened.push(w.label);
          // Nudge focus back to the launching tab so subsequent open()
          // calls aren't blocked by the browser's tab-focus popup gate.
          setTimeout(() => { try { popup.focus(); } catch (_e) { /* noop */ } }, 50);
        } else {
          failed.push(w.label);
        }
      });
      if (failed.length) {
        toast.error(`Popup blocked for: ${failed.join(", ")}. Allow popups for this site.`);
      } else {
        toast.success(`Deployed ${opened.length} command consoles`);
      }
    } catch (e) {
      toast.error(e?.message || "Could not deploy consoles");
    } finally {
      setBusy(false);
      setOpenMenu(false);
    }
  };

  return (
    <Popover open={openMenu} onOpenChange={setOpenMenu}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          disabled={busy}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-zinc-950 font-extrabold text-xs h-9 gap-1.5"
          data-testid="command-console-launcher-btn"
        >
          <Rocket className="h-4 w-4"/>
          {busy ? "Deploying…" : "Deploy 4-Window Console"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 w-72 p-3"
        align="end"
        data-testid="command-console-menu"
      >
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2 flex items-center gap-1.5">
          <Layout className="h-3 w-3"/>Deploy Layout
        </div>
        <div className="space-y-2">
          {/* v2.7.63+E48 · PWA-style Grid Console — recommended default. */}
          <button
            type="button"
            onClick={() => launch("grid")}
            className="w-full text-left p-2.5 rounded border-2 border-amber-400/60 hover:border-amber-300 bg-gradient-to-br from-amber-500/10 to-zinc-950 hover:from-amber-500/20 transition relative"
            data-testid="command-console-grid"
          >
            <div className="absolute top-1 right-1.5 px-1.5 py-0.5 rounded bg-amber-400 text-zinc-950 text-[8px] font-extrabold uppercase tracking-wider flex items-center gap-0.5">
              <Sparkles className="h-2.5 w-2.5"/>Recommended
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Grid3x3 className="h-4 w-4 text-amber-300"/>
              <span className="font-bold text-sm">Seamless Grid Console · PWA</span>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              One clean window · four panels in a 2×2 grid. Installable as
              its own app icon. Detach any panel to a dedicated window.
              Auth persists across all views.
            </div>
          </button>

          <div className="text-[9px] uppercase tracking-widest text-zinc-500 pt-1 pl-1">Legacy · Multi-Window</div>
          <button
            type="button"
            onClick={() => launch("quadrant")}
            className="w-full text-left p-2.5 rounded border border-zinc-800 hover:border-amber-400/50 hover:bg-zinc-900 transition"
            data-testid="command-console-quadrant"
          >
            <div className="flex items-center gap-2 mb-1">
              <Monitor className="h-4 w-4 text-amber-300"/>
              <span className="font-bold text-sm">2×2 Quadrant · Single Monitor</span>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              Splits the primary monitor into 4 separate popup windows.
              Best for 4K+ displays.
            </div>
          </button>
          <button
            type="button"
            onClick={() => launch("monitors")}
            className="w-full text-left p-2.5 rounded border border-zinc-800 hover:border-amber-400/50 hover:bg-zinc-900 transition"
            data-testid="command-console-monitors"
          >
            <div className="flex items-center gap-2 mb-1">
              <Monitor className="h-4 w-4 text-emerald-300"/>
              <span className="font-bold text-sm">1 Per Monitor · Multi-Screen</span>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              One console per monitor. Best for 3-4 side-by-side monitors.
              You&apos;ll be asked how many screens.
            </div>
          </button>
        </div>
        <div className="mt-3 flex items-start gap-1.5 text-[10px] text-zinc-500 border-t border-zinc-800 pt-2">
          <Info className="h-3 w-3 flex-shrink-0 mt-0.5 text-sky-300"/>
          <span>
            Auth persists across all 4 windows (no re-login required).
            Each window shows your Employee ID overlay so anyone can
            identify the current operator at a glance.
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
